import React from 'react';

/**
 * The stepper card beside the wizard — the same in all three frames of
 * 16370:191557, with a different step lit.
 *
 * A finished step is a green/350 disc with a tick, the open one a brand disc
 * with its number, the ones ahead a gray/100 disc with a gray/200 ring. Between
 * two steps runs the dashed slate/200 line, 86px long, centred under the disc.
 *
 * The frames disagree by a pixel here and there — a 34px disc beside a 36px
 * one, a title at 13px in one frame and 14 in the next. This draws one of each:
 * 36px, 14px, and the open step's lead in the brand as the first frame letters
 * it.
 *
 * Below `lg` there is not the width for a card beside the step, and the file
 * has no smaller frame: the three steps sit in a row over it, numbers and
 * titles only.
 */
const DISC = {
    done: 'tw-border-v2-student-done tw-bg-v2-student-done tw-text-white',
    current: 'tw-border-v2-brand tw-bg-v2-brand tw-text-white',
    upcoming: 'tw-border-v2-neutral-200 tw-bg-v2-neutral-100 tw-text-v2-neutral-400',
};

const TITLE = {
    done: 'tw-text-v2-neutral-900',
    current: 'tw-text-v2-neutral-900',
    upcoming: 'tw-text-v2-neutral-400',
};

const LEAD = {
    done: 'tw-text-v2-neutral-400',
    current: 'tw-text-v2-brand',
    upcoming: 'tw-text-v2-student-hint',
};

const WizardStepper = ({ steps, current, label, doneLabel, className = '' }) => (
    <nav
        aria-label={label}
        className={
            'tw-rounded-[20px] tw-border-[0.8px] tw-border-solid tw-border-v2-neutral-100 tw-bg-white ' +
            `tw-p-[16px] lg:tw-px-[28px] lg:tw-py-[24px] ${className}`
        }
    >
        <ol className="tw-m-0 tw-grid tw-list-none tw-grid-cols-3 tw-gap-[8px] tw-p-0 lg:tw-flex lg:tw-flex-col">
            {steps.map((step, index) => {
                let state = 'upcoming';
                if (index < current) state = 'done';
                else if (index === current) state = 'current';

                return (
                    <li key={step.key} aria-current={state === 'current' ? 'step' : undefined} className="tw-flex tw-flex-col">
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-[6px] tw-text-center lg:tw-flex-row lg:tw-items-start lg:tw-gap-[14px] lg:tw-text-start">
                            <span
                                aria-hidden
                                className={
                                    'tw-flex tw-size-[36px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full ' +
                                    `tw-border-[1.5px] tw-border-solid tw-text-[14px] tw-font-bold tw-leading-[1.6] ${DISC[state]}`
                                }
                            >
                                {state === 'done' ? '✓' : index + 1}
                            </span>
                            <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-[2px]">
                                <span className={`tw-text-[13px] tw-font-bold tw-leading-[1.6] lg:tw-text-[14px] ${TITLE[state]}`}>
                                    {step.title}
                                    {state === 'done' && <span className="tw-sr-only"> — {doneLabel}</span>}
                                </span>
                                <span className={`tw-hidden tw-text-[12px] tw-leading-[1.5] lg:tw-block ${LEAD[state]}`}>
                                    {step.lead}
                                </span>
                            </span>
                        </div>

                        {index < steps.length - 1 && (
                            <span aria-hidden className="tw-hidden tw-h-[91px] tw-w-[36px] tw-items-center tw-justify-center lg:tw-flex">
                                <span className="tw-h-[86px] tw-w-[2px] tw-bg-[repeating-linear-gradient(to_bottom,theme(colors.v2.line.mist)_0_4px,transparent_4px_8px)]" />
                            </span>
                        )}
                    </li>
                );
            })}
        </ol>
    </nav>
);

export default WizardStepper;
